import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/* Tiny month attendance grid used in Payroll.
   Props: employeeId, companyId, month (1-12), year (YYYY)
   Renders colored dots: green=present/wfh, yellow=late, red=absent/short/half_day, blue=leave (approved) */
const colorFor = (status) => {
  if (!status) return "bg-[#F1F5F9] text-[#64748B]"; // no record
  if (status === "leave") return "bg-[#2563EB]/15 text-[#1D4ED8]";
  if (status === "present" || status === "wfh") return "bg-[#12B886]/15 text-[#0B6F55]";
  if (status === "late") return "bg-[#FAB005]/20 text-[#946200]";
  if (status === "absent" || status === "short" || status === "half_day") return "bg-[#F54236]/15 text-[#991B1B]";
  return "bg-[#F1F5F9] text-[#64748B]";
};
const labelFor = (status) => {
  if (!status) return "";
  if (status === "wfh") return "W";
  if (status === "leave") return "L";
  return status[0].toUpperCase();
};

export default function AttendanceCalendar({ employeeId, companyId, month, year }) {
  const { user } = useAuth();
  const [records, setRecords] = useState([]);
  const [leaves, setLeaves] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!employeeId) return;
      const mm = String(month).padStart(2, "0");
      const fromDate = `${year}-${mm}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const toDate = `${year}-${mm}-${String(lastDay).padStart(2, "0")}`;
      try {
        const params = { employee_id: employeeId, from_date: fromDate, to_date: toDate };
        // Employees do not get to specify employee_id; backend filters anyway
        if (user?.role !== "employee") params.company_id = companyId;
        const [a, l] = await Promise.all([
          api.get("/attendance", { params }),
          api.get("/leaves", { params: { company_id: companyId, status: "approved" } }),
        ]);
        if (cancelled) return;
        setRecords(a.data);
        setLeaves(l.data.filter((x) => x.employee_id === employeeId));
      } catch {
        if (!cancelled) { setRecords([]); setLeaves([]); }
      }
    };
    run();
    return () => { cancelled = true; };
  }, [employeeId, companyId, month, year, user]);

  const daysInMonth = new Date(year, month, 0).getDate();
  const firstWeekday = new Date(year, month - 1, 1).getDay(); // 0 = Sun
  const byDate = Object.fromEntries(records.map((r) => [r.date, r]));

  const leaveSet = new Set();
  leaves.forEach((lv) => {
    const from = new Date(lv.from_date);
    const to = new Date(lv.to_date);
    for (let d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) {
      leaveSet.add(d.toISOString().slice(0, 10));
    }
  });

  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    let status = byDate[dateStr]?.status;
    if (!status && leaveSet.has(dateStr)) status = "leave";
    cells.push({ d, dateStr, status });
  }

  return (
    <div data-testid="attendance-calendar">
      <div className="grid grid-cols-7 gap-1 text-[10px] text-[#64748B] mb-1 font-semibold uppercase tracking-wider">
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (<div key={i} className="text-center">{d}</div>))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((c, i) => c === null ? (
          <div key={`b${i}`} className="aspect-square" />
        ) : (
          <div
            key={c.dateStr}
            className={`aspect-square rounded-sm flex flex-col items-center justify-center text-[10px] font-mono ${colorFor(c.status)}`}
            title={c.status ? `${c.dateStr} · ${c.status}` : c.dateStr}
            data-testid={`cal-${c.dateStr}`}
          >
            <span className="text-[9px] leading-none">{c.d}</span>
            {c.status && <span className="text-[8px] uppercase tracking-wider opacity-70">{labelFor(c.status)}</span>}
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-3 mt-3 text-[10px] text-[#475569]">
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[#12B886]/40 inline-block"/>Present</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[#FAB005]/40 inline-block"/>Late</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[#F54236]/40 inline-block"/>Absent/Short</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[#2563EB]/40 inline-block"/>Leave</span>
      </div>
    </div>
  );
}
